import { ComponentProps } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './button.module.scss';

export const sizeVariants = {
  large: styles.large,
  medium: styles.medium,
  icon: styles.icon,
  iconSmall: styles.iconSmall,
  small: styles.small,
  tiny: styles.tiny,
  mini: styles.mini
};

export const colorSchemeVariants = {
  primary: styles.primary,
  secondary: styles.secondary,
  tertiary: styles.tertiary,
  destructive: styles.destructive,
  ghost: styles.ghost,
  neutral: styles.neutral
} as const;

export const buttonClass = cva(styles.button, {
  variants: {
    size: sizeVariants,
    colorScheme: colorSchemeVariants,
    loading: {
      true: styles.loading
    }
  },
  defaultVariants: {
    colorScheme: 'primary',
    size: 'medium'
  }
});

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonClass> & {
    loading?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
  };

export default function Button({
  className,
  loading = false,
  colorScheme = 'primary',
  size = 'medium',
  startIcon = null,
  endIcon = null,
  children,
  ...props
}: ButtonProps) {
  const renderChildren = () => {
    if (startIcon || endIcon) {
      return (
        <div className={styles.withIcon}>
          {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
          <span>{children}</span>
          {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
        </div>
      );
    }
    return children;
  };

  if (loading) {
    return (
      <button
        className={buttonClass({
          className,
          colorScheme,
          size,
          loading
        })}
        {...props}
      >
        <div className={styles.spinner}>
          <LoadingSpinner size="small" />
        </div>
        <div className={styles.loadingText}>{renderChildren()}</div>
      </button>
    );
  }
  return (
    <button
      className={buttonClass({ className, colorScheme, size })}
      {...props}
    >
      {renderChildren()}
    </button>
  );
}
